import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hook";
import conversationLogger from "../conversationLogger";
import { setSelectedMessageIndex } from "../index";
import { cloneConversation, resetCloneConversationState } from "./cloneConversationSlice";
import { CloneConversationLogicErrorType, CloneConversationType } from "./types";
/**
 * This contains the logic fo r the cloning flow.
 * The user can decide to clone a conversation either fully or partially. Either way
 * we ultimately need the conversation id and the last message id until which the user wants to clone
 * the current conversation.
 * Expect the `conversation` state to exist and be a type or subtype of IConversationState and be updated accordingly.
 * This where this will read the 'selectedMessageIndex' value.
 */
export default function useCloneConversation() {
    const dispatch = useAppDispatch();
    //
    // Outside parameters below
    //
    // Store a copy of the conversation so that it can be cloned appropriately
    const [conversationMessages, setConversationMessages] = useState([]);
    // Param for cloning a new conversation
    const [cloneConversationTitle, setCloneConversationTitle] = useState("");
    //
    // Exposed data below
    //
    // User is required to choose between full cloning and partial cloning
    const [isUserChoiceFullPartialRequired, setIsUserChoiceFullPartialRequired] = useState(false);
    // User has started the partial cloning flow, we need them to give
    const [isUserChoiceLastMessageRequired, setIsUserChoiceLastMessageRequired] = useState(false);
    // Cloning result
    const [clonedConversationInfo, setClonedConversationInfo] = useState(null);
    const [cloningConversationError, setCloningConversationError] = useState(null);
    //
    // States updated externally below and their side effects
    //
    const { error: cloneConversationResultError, id: cloneConversationResultId, character_id: clonedConversationResultCharacterId, isLoading: isCloningConversationInProgress, } = useAppSelector((state) => state.cloneConversation);
    const { selectedMessageIndex } = useAppSelector((state) => state.conversation);
    // When we get success or error we reset the state
    // and update the exposed resulting state
    useEffect(() => {
        if (cloneConversationResultId !== "" || cloneConversationResultError) {
            reset();
        }
        if (cloneConversationResultId !== "") {
            setClonedConversationInfo({
                id: cloneConversationResultId,
                character_id: clonedConversationResultCharacterId,
            });
        }
        if (cloneConversationResultError) {
            setCloningConversationError(cloneConversationResultError);
        }
        // everything has been reset in the store
        if (cloneConversationResultId === "" && clonedConversationResultCharacterId === "" && cloneConversationResultError === null) {
            setClonedConversationInfo(null);
            setCloningConversationError(null);
        }
    }, [clonedConversationResultCharacterId, cloneConversationResultId, cloneConversationResultError]);
    //
    // Exposed methods
    //
    /**
     * When the user select trigger the clone conversation process (e.g. from a menu)
     */
    const onUserStartCloning = (conversation) => {
        onResetCloneForNextUse();
        setConversationMessages(conversation);
        setIsUserChoiceFullPartialRequired(true);
    };
    /**
     * The user has decided to cancel the coning process (e.g. by cancelling a modal)
     */
    const onUserCancelCloning = () => {
        onResetCloneForNextUse();
    };
    /**
     * The user has selected the type of conversation cloning full or partial.
     * If it is a full conversation we will proceed to dispatch the conversation event.
     * if it is partial, we will 'save' the conversation title and the conversation id.
     * @param type - the type of cloning that use has chosen
     * @param title - the title for the newly cloned conversation
     */
    const onUserSelectedCloningType = (type, title) => {
        const message = conversationMessages[conversationMessages.length - 1];
        switch (type) {
            case CloneConversationType.FULL:
                // The lastMessageId is the last message of the conversation
                if (message) {
                    performCloning(message.id, title, CloneConversationType.FULL);
                }
                else {
                    setCloningConversationError(new Error(`${CloneConversationLogicErrorType.ERROR_FULL}: Unable to clone this conversation, last message not found`));
                }
                break;
            case CloneConversationType.PARTIAL:
                setCloneConversationTitle(title);
                setIsUserChoiceLastMessageRequired(true);
                setIsUserChoiceFullPartialRequired(false);
                break;
            case CloneConversationType.CANCEL:
                onResetCloneForNextUse();
                break;
            default:
                conversationLogger.error(`Unknown Action on Cloning Modal: ${type}`);
                setCloningConversationError(new Error(`${CloneConversationLogicErrorType.ERROR_GENERIC}: Unable to clone this conversation, unknown cloning type ${type}`));
                break;
        }
    };
    /**
     * After selecting the last message, the user has confirmed they want to proceed with the cloning.
     * We perform the cloning operation with the title we have saved earlier in the flow.
     */
    const onUserConfirmPartialCloning = () => {
        const message = conversationMessages[selectedMessageIndex];
        if (message) {
            performCloning(message.id, cloneConversationTitle, CloneConversationType.PARTIAL);
        }
        else {
            setCloningConversationError(new Error(`${CloneConversationLogicErrorType.ERROR_PARTIAL}: Unable to clone this conversation, last message not found`));
        }
    };
    /**
     * For the cloning operation selecting one message will select all the messages before
     * @param currentIndex - the current index of the message in the conversation
     * @returns {boolean} - true if the current message should be marked as selected, false otherwise.
     */
    const shouldCurrentMessageBeSelectedForCloning = (currentIndex) => {
        return selectedMessageIndex !== null && currentIndex <= selectedMessageIndex;
    };
    /**
     * when the flow successfully finishes (notification displayed or any async process) we reset all states
     */
    const onResetCloneForNextUse = () => {
        reset();
        dispatch(resetCloneConversationState());
    };
    //
    // Internal methods below
    //
    // When we have both the title (that comes from the cloning modal)
    // and the message id
    // AND the user has confirmed (in the case of partial cloning they may change the message to clone up until)
    // => we trigger the cloning
    const performCloning = (lastMessageId, convTitle, cloningType) => {
        const message = conversationMessages[conversationMessages.length - 1];
        // here the only use case possible if ofr the title to be incorrect,
        // if message or lastMessageId were invalid, an error would have been raised earlier
        if (!message || !lastMessageId || !convTitle || convTitle === "") {
            setCloningConversationError(new Error(`${CloneConversationLogicErrorType.ERROR_EMPTY_TITLE}: 
                    Unable to clone this conversation, the title is empty`));
            return;
        }
        dispatch(cloneConversation({
            conversation_id: message.conversation_id,
            message_id: lastMessageId,
            label: convTitle,
            type: cloningType,
        }));
    };
    const setLastMsgIndexForPartialCloning = (idx) => {
        dispatch(setSelectedMessageIndex(idx));
    };
    // Internal reset function
    const reset = () => {
        setCloningConversationError(null);
        setIsUserChoiceFullPartialRequired(false);
        setIsUserChoiceLastMessageRequired(false);
        setCloneConversationTitle("");
        setLastMsgIndexForPartialCloning(-1);
    };
    return {
        isUserChoiceFullPartialRequired,
        isUserChoiceLastMessageRequired,
        onUserStartCloning,
        onUserCancelCloning,
        onUserSelectedCloningType,
        onUserConfirmPartialCloning,
        onResetCloneForNextUse,
        clonedConversationInfo,
        cloningConversationError,
        isCloningConversationInProgress,
        shouldCurrentMessageBeSelectedForCloning,
        setLastMsgIndexForPartialCloning,
        selectedMessageIndex,
    };
}
